import { eachDayOfInterval, format, parse } from "date-fns";
import { memoize } from "lodash";
import { useMemo } from "react";
import { errorLog } from "../../../logger";
import { dateFormat } from "../Shared";
import filterDataAfterDate from "../utilities/filterDataAfterDate";
import { smoothEndingZeroBalances } from "../utilities/smoothEndingZeroBalances";
import useGetBalances from "./SummaryScreen/useGetBalances";
import useGetCumulativeDailyNetDeposits from "./SummaryScreen/useGetNetDeposits";
import useGetDatesForTimeFrame from "./useGetDatesForTimeFrame";
export const memoizedEachDayOfInterval = memoize((startDate, endDate) => eachDayOfInterval({
    start: parse(startDate, dateFormat, new Date()),
    end: parse(endDate, dateFormat, new Date()),
}), (startDate, endDate) => `${startDate}-${endDate}`);
export const memoizedDayMapper = memoize((startDate, endDate, balances, deposits, _groupId) => {
    const days = memoizedEachDayOfInterval(startDate, endDate);
    const data = days.map((d) => {
        const search = {
            startDate: startDate,
            endDate: format(d, dateFormat),
        };
        const balancePoint = balances.find((x) => x.startDate === search.startDate && x.endDate === search.endDate);
        const depositPoint = deposits.find((x) => x.date === search.endDate);
        return {
            startDate: search.startDate,
            endDate: search.endDate,
            balanceOnStartDay: balancePoint?.balanceStart ?? 0,
            balanceOnEndDay: balancePoint?.balanceEnd ?? 0,
            returnsSinceStartDay: balancePoint?.returns ?? 0,
            netGainsSinceStartDay: balancePoint?.netGains ?? 0,
            depositsSinceStartDay: depositPoint?.contributions ?? 0,
            withdrawalsSinceStartDay: depositPoint?.withdrawals ?? 0,
            feesSinceStartDay: balancePoint?.fees ?? 0,
            netAdditions: balancePoint?.netAdditions ?? 0,
        };
    });
    return data;
}, (startDate, endDate, balances, _deposits, // ignored in the key generation
groupId) => {
    // balances.length tells us how many data (date) points we have between the
    // start and end date. For example, for 1 year do we ask for daily balances
    // or weekly balances, the difference is in either 52 points (weekly) or
    // 365 points (daily).
    const dateSegment = `${startDate}->${endDate}-${balances.length}`;
    return `${groupId}_${dateSegment}`;
});
// undefined means we're still loading the data
export default function usePerformanceGroupSummary(groupId, timeFrame, customTimeFrame, personId, authConfig, lastDateToLookTill) {
    const dates = useGetDatesForTimeFrame(groupId, timeFrame, customTimeFrame, personId, authConfig);
    const balances = useGetBalances(groupId, timeFrame, customTimeFrame, personId, authConfig);
    const deposits = useGetCumulativeDailyNetDeposits(groupId, timeFrame, customTimeFrame, personId, authConfig);
    const result = useMemo(() => {
        const allDataLoaded = dates.data !== undefined &&
            balances.data !== undefined &&
            deposits.data !== undefined;
        const isAnyLoading = dates.isLoading || balances.isLoading || deposits.isLoading;
        const hasAnyError = dates.hasError || balances.hasError || deposits.hasError;
        if (!allDataLoaded && isAnyLoading) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (!allDataLoaded && hasAnyError) {
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: dates.hasError
                    ? dates.error
                    : balances.hasError
                        ? balances.error
                        : deposits.hasError
                            ? deposits.error
                            : new Error("Unknown error"),
            };
        }
        // wrap it in a try because eachDayOfInterval can throw if you try to use the same date as start & end
        try {
            const data = memoizedDayMapper(dates.data.startDate, dates.data.endDate, balances.data, deposits.data, groupId);
            const filteredData = filterDataAfterDate(data, lastDateToLookTill);
            const smoothedData = smoothEndingZeroBalances(filteredData);
            const start = smoothedData.slice(-1)[0]?.startDate;
            const end = smoothedData.slice(-1)[0]?.endDate;
            return {
                data: {
                    timeFrame,
                    data: smoothedData,
                    startDate: start ?? dates.data.startDate,
                    endDate: end ?? dates.data.endDate,
                },
                isLoading: false,
                hasError: false,
            };
        }
        catch (e) {
            if (e instanceof Error) {
                errorLog("[usePerformanceGroupSummary]", `${e.name}, ${e.message}`);
            }
            else {
                errorLog("[usePerformanceGroupSummary]", "error");
            }
            return { data: undefined, isLoading: false, hasError: true, error: e };
        }
    }, [balances, dates, deposits, groupId, lastDateToLookTill, timeFrame]);
    return result;
}
//# sourceMappingURL=usePerformanceGroupSummary.js.map